"use client";

import { Activity, ArrowRight, HeartPulse, ShieldCheck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FitnessTesting() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="fitnesstestingoverview" data-nav="light" ref={ref} style={{
    opacity: isVisible ? 1 : 0
  }} className="bg-background text-foreground py-20 lg:py-28 transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Fitness testing for better-informed training
          </h2>
          <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed">
            For active clients and athletes, VO₂ testing provides objective physiological information that can help guide exercise intensity and monitor training adaptations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          <Card className="border border-border/80 bg-card shadow-xs">
            <CardContent className="p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-6">
                  <Zap className="size-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-card-foreground mb-3">
                  Athletic Performance & Conditioning
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Identify precise aerobic thresholds, metabolic efficiency, and maximal oxygen uptake. Testing removes guesswork from interval targets, pacing, and recovery zones.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-border flex items-center gap-2 text-sm text-muted-foreground">
                <Activity className="size-4 text-primary" />
                <span>Precision heart rate and power zone calibration</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/80 bg-card shadow-xs">
            <CardContent className="p-8 flex flex-col justify-between h-full">
              <div>
                <div className="w-12 h-12 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center mb-6">
                  <HeartPulse className="size-6 text-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-card-foreground mb-3">
                  Healthy Ageing & Cardiorespiratory Health
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  The very same clinical technology serves individuals aiming for lifelong vitality. Cardiorespiratory fitness is one of the strongest clinical predictors of long-term mobility and sustained cardiovascular wellness.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-border flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck className="size-4 text-primary" />
                <span>Safely progressive exercise for longevity</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-4 border-t border-border">
          <Button asChild size="lg">
            <Link to="/VO2MaxMetabolicTestingPage" className="inline-flex items-center gap-2">
              VO₂ Max & Metabolic Testing
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/BiomechanicsExpertisePage">
              Biomechanics Expertise
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}